import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Check, ExternalLink, Pencil, X, Shield, Loader2, RefreshCw } from "lucide-react";
import { SiYoutube, SiFacebook, SiX, SiInstagram, SiLinkedin, SiPinterest, SiTiktok } from "react-icons/si";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocationStore } from "@/lib/store";
import { useToast } from "@/hooks/use-toast";
import type { Location } from "@shared/schema";
import { RtdOptOut, RtdOptOutBanner } from "@/components/rtd-opt-out";

const SOCIAL_OPT_OUT_TASKS = ["social_sync", "missing_profiles"];

type PlatformKey = "youtube" | "facebook" | "twitter" | "instagram" | "linkedin" | "pinterest" | "tiktok";

interface PlatformConfig {
  key: PlatformKey;
  name: string;
  icon: typeof SiYoutube;
  color: string;
  bgColor: string;
  signupUrl: string;
}

const platforms: PlatformConfig[] = [
  { key: "youtube", name: "YouTube", icon: SiYoutube, color: "text-red-500", bgColor: "bg-red-500/10", signupUrl: "https://www.youtube.com/create_channel" },
  { key: "facebook", name: "Facebook", icon: SiFacebook, color: "text-blue-500", bgColor: "bg-blue-500/10", signupUrl: "https://www.facebook.com/pages/create" },
  { key: "twitter", name: "Twitter / X", icon: SiX, color: "text-white", bgColor: "bg-neutral-800", signupUrl: "https://twitter.com/i/flow/signup" },
  { key: "instagram", name: "Instagram", icon: SiInstagram, color: "text-pink-500", bgColor: "bg-pink-500/10", signupUrl: "https://www.instagram.com/accounts/emailsignup/" },
  { key: "linkedin", name: "LinkedIn", icon: SiLinkedin, color: "text-blue-600", bgColor: "bg-blue-600/10", signupUrl: "https://www.linkedin.com/company/setup/new/" },
  { key: "pinterest", name: "Pinterest", icon: SiPinterest, color: "text-red-600", bgColor: "bg-red-600/10", signupUrl: "https://www.pinterest.com/business/create/" },
  { key: "tiktok", name: "TikTok", icon: SiTiktok, color: "text-white", bgColor: "bg-neutral-800", signupUrl: "https://www.tiktok.com/signup" },
];

type SocialProfiles = Partial<Record<PlatformKey, string>>;

function extractHandle(url: string, platform: PlatformKey): string {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split("/").filter(Boolean);
    if (platform === "youtube") {
      const chan = segments.find(s => s.startsWith("@")) || segments[segments.length - 1];
      return chan || parsed.hostname;
    }
    return segments[segments.length - 1] || parsed.hostname;
  } catch {
    return url;
  }
}

function SocialPreview({ url, platform }: { url: string; platform: PlatformConfig }) {
  const handle = extractHandle(url, platform.key);
  const Icon = platform.icon;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="block mt-3 rounded-lg border border-[#2A2A3A] bg-[#0D0D14] p-3 hover:border-[#3A3A4A] transition-colors group"
      data-testid={`preview-${platform.key}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-8 h-8 rounded-lg ${platform.bgColor} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 ${platform.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate group-hover:text-foreground transition-colors">
            {handle.startsWith("@") ? handle : `@${handle}`}
          </p>
          <p className="text-[11px] text-muted-foreground truncate">{platform.name} Profile</p>
        </div>
        <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <div className="mt-2 pt-2 border-t border-[#2A2A3A]">
        <p className="text-[11px] text-muted-foreground truncate">{url}</p>
      </div>
    </a>
  );
}

export default function SocialPage() {
  const { selectedLocationId } = useLocationStore();
  const { toast } = useToast();
  const [editingPlatform, setEditingPlatform] = useState<PlatformKey | null>(null);
  const [editUrl, setEditUrl] = useState("");

  const { data: locations } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations?.[0]?.id;

  const { data: socialProfiles, isLoading } = useQuery<SocialProfiles>({
    queryKey: ["/api/locations", locationId, "social-profiles"],
    enabled: !!locationId,
  });

  const mutation = useMutation({
    mutationFn: async (profiles: SocialProfiles) => {
      const res = await apiRequest("PUT", `/api/locations/${locationId}/social-profiles`, profiles);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "social-profiles"] });
      setEditingPlatform(null);
      setEditUrl("");
      toast({ title: "Profile updated", description: "Social profile saved successfully." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const syncGbpMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/locations/${locationId}/social-profiles/sync-gbp`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "social-profiles"] });
      if (data.synced > 0) {
        toast({ title: `Imported ${data.synced} social profile(s) from GBP` });
      } else {
        toast({ title: data.message || "No new social profiles found on your GBP" });
      }
    },
    onError: (err: Error) => {
      toast({ title: "Sync failed", description: err.message, variant: "destructive" });
    },
  });

  const connectedCount = socialProfiles ? Object.values(socialProfiles).filter(Boolean).length : 0;
  const totalPlatforms = platforms.length;
  const progressPercent = (connectedCount / totalPlatforms) * 100;

  function handleSave(key: PlatformKey) {
    const current = { ...(socialProfiles || {}) };
    if (editUrl.trim()) {
      current[key] = editUrl.trim();
    } else {
      delete current[key];
    }
    mutation.mutate(current);
  }

  function handleRemove(key: PlatformKey) {
    const current = { ...(socialProfiles || {}) };
    delete current[key];
    mutation.mutate(current);
  }

  function startEdit(key: PlatformKey) {
    setEditingPlatform(key);
    setEditUrl(socialProfiles?.[key] || "");
  }

  function cancelEdit() {
    setEditingPlatform(null);
    setEditUrl("");
  }

  if (!locationId) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <p className="text-muted-foreground" data-testid="text-no-location">Select a location to manage social profiles.</p>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-social-title">Social Fortress</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage the social profiles linked to your Google Business Profile
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RtdOptOut
            locationId={locationId}
            taskIds={SOCIAL_OPT_OUT_TASKS}
            stageName="Social Fortress"
            warningTitle="Skip social profiles for now?"
            warningBody={
              <p>
                Heads up — social profiles are some of the most powerful citation signals for local SEO.
                Google looks at consistent business info across YouTube, Facebook, Instagram, LinkedIn and
                others to confirm your business is real and trustworthy. Skipping them usually means slower
                ranking growth.
              </p>
            }
          />
          <Button
            variant="outline"
            size="sm"
            className="text-xs"
            onClick={() => syncGbpMutation.mutate()}
            disabled={syncGbpMutation.isPending}
            data-testid="button-sync-gbp-socials"
          >
            {syncGbpMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            )}
            {syncGbpMutation.isPending ? "Syncing..." : "Sync from GBP"}
          </Button>
        </div>
      </div>

      <RtdOptOutBanner
        locationId={locationId}
        taskIds={SOCIAL_OPT_OUT_TASKS}
        stageName="Social Fortress"
      />

      <Card className="border-[#1E1E2E] bg-[#12121A]" data-testid="card-fortress-score">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold" data-testid="text-fortress-progress">
                  {connectedCount}/{totalPlatforms} Social Profiles Active
                </h2>
                <Badge
                  className={
                    connectedCount === totalPlatforms
                      ? "bg-emerald-500/20 text-emerald-400"
                      : connectedCount >= 4
                        ? "bg-yellow-500/20 text-yellow-400"
                        : "bg-red-500/20 text-red-400"
                  }
                  data-testid="badge-fortress-status"
                >
                  {connectedCount === totalPlatforms ? "Fortress Complete" : connectedCount >= 4 ? "Good Progress" : "Needs Attention"}
                </Badge>
              </div>
              <Progress value={progressPercent} className="mt-2 h-2" data-testid="progress-fortress" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground" data-testid="text-seo-benefit">
            GBPs with active social profiles rank higher in local search. Connect all 7 platforms to maximize your Social Fortress and boost your local SEO ranking.
          </p>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {platforms.map((platform) => {
            const url = socialProfiles?.[platform.key];
            const isConnected = !!url;
            const isEditing = editingPlatform === platform.key;
            const Icon = platform.icon;

            return (
              <Card
                key={platform.key}
                className={`border-[#1E1E2E] bg-[#12121A] transition-colors ${isConnected ? "border-emerald-500/30" : ""}`}
                data-testid={`card-social-${platform.key}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-11 h-11 rounded-xl ${platform.bgColor} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${platform.color}`} />
                    </div>
                    {isConnected && !isEditing && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => startEdit(platform.key)}
                          data-testid={`button-edit-${platform.key}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-red-400 hover:text-red-300"
                          onClick={() => handleRemove(platform.key)}
                          data-testid={`button-remove-${platform.key}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>

                  <h3 className="text-sm font-semibold mb-2">{platform.name}</h3>

                  {isEditing ? (
                    <div className="space-y-2">
                      <Input
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder={`https://${platform.key === "twitter" ? "x.com" : platform.key + ".com"}/your-profile`}
                        className="text-xs h-8"
                        data-testid={`input-url-${platform.key}`}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 h-7 text-xs"
                          onClick={() => handleSave(platform.key)}
                          disabled={mutation.isPending}
                          data-testid={`button-save-${platform.key}`}
                        >
                          {mutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={cancelEdit}
                          data-testid={`button-cancel-${platform.key}`}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : isConnected ? (
                    <div className="space-y-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs" data-testid={`badge-connected-${platform.key}`}>
                        <Check className="w-3 h-3 mr-1" />
                        Connected
                      </Badge>
                      <SocialPreview url={url!} platform={platform} />
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Badge className="bg-muted text-muted-foreground text-xs" data-testid={`badge-notconnected-${platform.key}`}>
                        Not Connected
                      </Badge>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs"
                          onClick={() => startEdit(platform.key)}
                          data-testid={`button-add-${platform.key}`}
                        >
                          <Pencil className="w-3 h-3 mr-1.5" />
                          Add Profile URL
                        </Button>
                        <a
                          href={platform.signupUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full"
                          data-testid={`link-create-${platform.key}`}
                        >
                          <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground">
                            <ExternalLink className="w-3 h-3 mr-1.5" />
                            Create {platform.name} Page
                          </Button>
                        </a>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
